import { useCallback, useEffect, type ReactNode } from 'react'
import * as React from 'react'

import { ChertoExperiments } from 'constants/experiments'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isInsideCancellationOptionCutoff } from 'lib/offer/cancellationOptionPricing'
import { getAllPackageViews } from 'selectors/offerPage/offerPageSelectors'

import FlashCancellationOptionSelector from './FlashCancellationOptionSelector'
import useFlashCancellationOptionRows from './useFlashCancellationOptionRows'

interface Props {
  packageView: App.OfferPackageOptionView
  offer: App.HotelOffer
  allPackageViews: Array<App.OfferPackageOptionView>
  checkIn?: string
  /** Rendered in place of the options when the package sells only one, so the policy line keeps its spot. */
  fallback?: ReactNode
}

export function FlashCancellationOptionPicker(props: Props) {
  const { packageView, offer, allPackageViews, checkIn, fallback } = props
  const offerPageDispatch = useOfferPageDispatch()
  const { triggerActivation } = useExperiment(ChertoExperiments.flashCancellationOptions, { canActivate: false })
  const { baseKey, views, rows, reserveForZeroKeys, isPurchase, showsOptions } = useFlashCancellationOptionRows({
    packageView,
    offer,
    allPackageViews,
    checkIn,
  })

  const soldPolicies = new Set([
    packageView.package.roomRate?.cancellationPolicy?.type,
    ...(packageView.package.roomRate?.soldCancellationPolicies ?? []),
  ].filter(Boolean))
  const sellsOptions = isPurchase && soldPolicies.size > 1 && !isInsideCancellationOptionCutoff(checkIn, offer.property?.timezone)
  useEffect(() => {
    if (sellsOptions) {
      triggerActivation()
    }
  }, [sellsOptions, triggerActivation])

  const onChange = useCallback((key: string) => {
    offerPageDispatch({
      type: OfferPageActions.setCancellationOption,
      baseKey,
      policy: views.find(view => view.package.uniqueKey === key)?.package.cancellationOptionPolicy,
    })
  }, [baseKey, views, offerPageDispatch])

  if (!showsOptions) {
    return <>{fallback ?? null}</>
  }

  return <FlashCancellationOptionSelector
    rows={rows}
    reserveForZeroKeys={reserveForZeroKeys}
    checkIn={checkIn}
    propertyTimezoneOffset={offer.property?.timezoneOffset}
    numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
    onChange={onChange}
  />
}

type MappedProps = Pick<Props, 'offer' | 'allPackageViews' | 'checkIn'>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  allPackageViews: getAllPackageViews(state, appState),
  checkIn: state.checkInDate,
}), FlashCancellationOptionPicker)
